/**
 * SettingsPanel - Device settings including velocity configuration and calibration
 * Communicates with master via JSON commands for structured data
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { getSerialConnection, parseJsonFromCliResponse } from '@/lib/serialConnection';

interface VelocityConfig {
  press_fast: number;
  press_slow: number;
  release_fast: number;
  release_slow: number;
}

interface VelCalStatus {
  active: boolean;
  module: number | undefined;
  phase: 'idle' | 'fast' | 'slow' | 'done' | 'abort' | undefined;
  samples: number | undefined;
  samples_needed: number | undefined;
}

interface ModuleInfo {
  id: number;
  online: boolean;
  calibrated: boolean;
}

const DEFAULT_VELOCITY: VelocityConfig = {
  press_fast: 2,
  press_slow: 60,
  release_fast: 15,
  release_slow: 100,
};

export function SettingsPanel() {
  const [velocity, setVelocity] = useState<VelocityConfig>(DEFAULT_VELOCITY);
  const [velCal, setVelCal] = useState<VelCalStatus>({ active: false, module: undefined, phase: undefined, samples: undefined, samples_needed: undefined });
  const [modules, setModules] = useState<ModuleInfo[]>([]);
  const [selectedModule, setSelectedModule] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const pollingRef = useRef<number | null>(null);

  // Send JSON command and parse response
  const sendJsonCommand = useCallback(async (subcommand: string): Promise<unknown | null> => {
    const conn = getSerialConnection();
    if (!conn.isConnected()) return null;

    const response = await conn.sendCommand(`json ${subcommand}`);
    return parseJsonFromCliResponse(response.raw);
  }, []);

  // Fetch current velocity config
  const fetchVelocity = useCallback(async () => {
    const data = await sendJsonCommand('vel') as { type: string; press_fast: number; press_slow: number; release_fast: number; release_slow: number } | null;
    if (data && data.type === 'velocity') {
      setVelocity({
        press_fast: data.press_fast,
        press_slow: data.press_slow,
        release_fast: data.release_fast,
        release_slow: data.release_slow,
      });
    }
  }, [sendJsonCommand]);

  // Fetch velocity calibration status
  const fetchVelCal = useCallback(async () => {
    const data = await sendJsonCommand('velcal') as VelCalStatus & { type: string } | null;
    if (data && data.type === 'velcal') {
      setVelCal({
        active: data.active,
        module: data.module,
        phase: data.phase,
        samples: data.samples,
        samples_needed: data.samples_needed,
      });
    }
  }, [sendJsonCommand]);

  // Fetch module list
  const fetchModules = useCallback(async () => {
    const data = await sendJsonCommand('modules') as { type: string; list?: ModuleInfo[] } | null;
    if (data && data.type === 'modules' && data.list) {
      const list = data.list;
      setModules(list);
      // Select first online module if none selected
      const firstModule = list[0];
      if (list.length > 0 && firstModule && !list.find(m => m.id === selectedModule)) {
        setSelectedModule(firstModule.id);
      }
    }
  }, [sendJsonCommand, selectedModule]);

  // Track connection state and fetch data when connected
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    const conn = getSerialConnection();

    // Subscribe to connection state changes
    const unsubscribe = conn.onStateChange((state) => {
      const connected = state === 'connected';
      setIsConnected(connected);

      // Fetch data when connection is established
      if (connected) {
        fetchVelocity();
        fetchModules();
        fetchVelCal();
      }
    });

    return () => {
      unsubscribe();
    };
  }, [fetchVelocity, fetchModules, fetchVelCal]);

  // Poll during calibration
  useEffect(() => {
    if (velCal.active) {
      pollingRef.current = window.setInterval(() => {
        fetchVelCal();
      }, 500);
    } else {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [velCal.active, fetchVelCal]);

  // Apply velocity config
  const handleApplyVelocity = async () => {
    const conn = getSerialConnection();
    if (!conn.isConnected()) {
      setMessage({ type: 'error', text: 'Not connected' });
      return;
    }

    setLoading(true);
    setMessage(null);

    const cmd = `velcal set ${velocity.press_fast} ${velocity.press_slow} ${velocity.release_fast} ${velocity.release_slow}`;
    const response = await conn.sendCommand(cmd);

    if (response.success || response.raw.includes('OK:')) {
      setMessage({ type: 'success', text: 'Velocity config applied' });
      // Refresh to confirm
      await fetchVelocity();
    } else {
      setMessage({ type: 'error', text: response.message || 'Failed to apply' });
    }

    setLoading(false);
  };

  // Reset to defaults
  const handleResetDefaults = async () => {
    const conn = getSerialConnection();
    if (!conn.isConnected()) {
      setMessage({ type: 'error', text: 'Not connected' });
      return;
    }

    setLoading(true);
    setMessage(null);

    const response = await conn.sendCommand('velcal reset');

    if (response.success || response.raw.includes('OK:')) {
      setMessage({ type: 'success', text: 'Reset to defaults' });
      await fetchVelocity();
    } else {
      setMessage({ type: 'error', text: response.message || 'Failed to reset' });
    }

    setLoading(false);
  };

  // Start velocity calibration
  const handleStartVelCal = async () => {
    const conn = getSerialConnection();
    if (!conn.isConnected()) {
      setMessage({ type: 'error', text: 'Not connected' });
      return;
    }

    setLoading(true);
    setMessage(null);

    const response = await conn.sendCommand(`velcal start ${selectedModule}`);

    if (response.success || response.raw.includes('OK:')) {
      setMessage({ type: 'success', text: 'Calibration started - hit keys FAST' });
      await fetchVelCal();
    } else {
      setMessage({ type: 'error', text: response.message || 'Failed to start' });
    }

    setLoading(false);
  };

  // Abort velocity calibration
  const handleAbortVelCal = async () => {
    const conn = getSerialConnection();
    if (!conn.isConnected()) return;

    await conn.sendCommand('velcal abort');
    await fetchVelCal();
    setMessage({ type: 'success', text: 'Calibration aborted' });
  };

  // Start key position calibration
  const handleStartKeyCal = async () => {
    const conn = getSerialConnection();
    if (!conn.isConnected()) {
      setMessage({ type: 'error', text: 'Not connected' });
      return;
    }

    setLoading(true);
    setMessage(null);

    const response = await conn.sendCommand(`cal start ${selectedModule}`);

    if (response.success || response.raw.includes('OK:')) {
      setMessage({ type: 'success', text: 'Key calibration started - follow LED colors' });
    } else {
      setMessage({ type: 'error', text: response.message || 'Failed to start' });
    }

    setLoading(false);
  };

  // Get phase color for indicator
  const getPhaseColor = (phase?: string) => {
    switch (phase) {
      case 'fast': return '#dc3545'; // Red
      case 'slow': return '#28a745'; // Green
      case 'done': return '#007bff'; // Blue
      default: return '#6c757d'; // Gray
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Connection warning */}
      {!isConnected && (
        <div style={warningStyle}>
          Connect to device first (use the connection bar above)
        </div>
      )}

      {/* Message display */}
      {message && (
        <div style={{
          padding: '8px 12px',
          borderRadius: '4px',
          fontSize: '12px',
          backgroundColor: message.type === 'success' ? '#d4edda' : '#f8d7da',
          color: message.type === 'success' ? '#155724' : '#721c24',
          border: `1px solid ${message.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
        }}>
          {message.text}
        </div>
      )}

      {/* Velocity Configuration Section */}
      <div style={sectionStyle}>
        <h4 style={sectionTitleStyle}>Velocity Configuration</h4>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <div>
            <label style={labelStyle}>Press Fast (max vel)</label>
            <div style={inputGroupStyle}>
              <input
                type="number"
                min={1}
                max={500}
                value={velocity.press_fast}
                onChange={(e) => setVelocity(v => ({ ...v, press_fast: parseInt(e.target.value) || 1 }))}
                disabled={!isConnected || loading}
                style={inputStyle}
              />
              <span style={unitStyle}>ms</span>
            </div>
          </div>

          <div>
            <label style={labelStyle}>Press Slow (min vel)</label>
            <div style={inputGroupStyle}>
              <input
                type="number"
                min={10}
                max={2000}
                value={velocity.press_slow}
                onChange={(e) => setVelocity(v => ({ ...v, press_slow: parseInt(e.target.value) || 60 }))}
                disabled={!isConnected || loading}
                style={inputStyle}
              />
              <span style={unitStyle}>ms</span>
            </div>
          </div>

          <div>
            <label style={labelStyle}>Release Fast</label>
            <div style={inputGroupStyle}>
              <input
                type="number"
                min={1}
                max={500}
                value={velocity.release_fast}
                onChange={(e) => setVelocity(v => ({ ...v, release_fast: parseInt(e.target.value) || 15 }))}
                disabled={!isConnected || loading}
                style={inputStyle}
              />
              <span style={unitStyle}>ms</span>
            </div>
          </div>

          <div>
            <label style={labelStyle}>Release Slow</label>
            <div style={inputGroupStyle}>
              <input
                type="number"
                min={10}
                max={2000}
                value={velocity.release_slow}
                onChange={(e) => setVelocity(v => ({ ...v, release_slow: parseInt(e.target.value) || 100 }))}
                disabled={!isConnected || loading}
                style={inputStyle}
              />
              <span style={unitStyle}>ms</span>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
          <button
            onClick={handleApplyVelocity}
            disabled={!isConnected || loading}
            style={buttonStyle}
          >
            Apply & Save
          </button>
          <button
            onClick={handleResetDefaults}
            disabled={!isConnected || loading}
            style={{ ...buttonStyle, backgroundColor: '#6c757d' }}
          >
            Reset Defaults
          </button>
          <button
            onClick={fetchVelocity}
            disabled={!isConnected || loading}
            style={{ ...buttonStyle, backgroundColor: '#17a2b8' }}
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Velocity Auto-Calibration Section */}
      <div style={sectionStyle}>
        <h4 style={sectionTitleStyle}>Velocity Auto-Calibration</h4>

        <div style={{ marginBottom: '12px' }}>
          <label style={labelStyle}>Target Module</label>
          <select
            value={selectedModule}
            onChange={(e) => setSelectedModule(parseInt(e.target.value))}
            disabled={!isConnected || loading || velCal.active}
            style={{ ...inputStyle, width: '100%' }}
          >
            {modules.length === 0 ? (
              <option value={0}>No modules found</option>
            ) : (
              modules.map(m => (
                <option key={m.id} value={m.id}>
                  Module {m.id} {m.online ? '(online)' : '(offline)'}
                </option>
              ))
            )}
          </select>
        </div>

        {/* Calibration status */}
        {velCal.active && (
          <div style={{
            padding: '12px',
            backgroundColor: '#f8f9fa',
            borderRadius: '4px',
            marginBottom: '12px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <div style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: getPhaseColor(velCal.phase),
                animation: 'pulse 1s infinite',
              }} />
              <span style={{ fontWeight: 600, textTransform: 'uppercase' }}>
                {velCal.phase === 'fast' ? 'Hit keys FAST!' : 'Hit keys SLOW!'}
              </span>
            </div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              Module: {velCal.module} | Samples: {velCal.samples}/{velCal.samples_needed}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: '8px' }}>
          {!velCal.active ? (
            <button
              onClick={handleStartVelCal}
              disabled={!isConnected || loading || modules.length === 0}
              style={{ ...buttonStyle, backgroundColor: '#28a745' }}
            >
              Start Velocity Calibration
            </button>
          ) : (
            <button
              onClick={handleAbortVelCal}
              disabled={!isConnected}
              style={{ ...buttonStyle, backgroundColor: '#dc3545' }}
            >
              Abort Calibration
            </button>
          )}
        </div>

        <p style={{ fontSize: '11px', color: '#666', marginTop: '8px' }}>
          1. RED LEDs: Hit 3 keys as FAST as possible<br />
          2. GREEN LEDs: Hit 3 keys as SLOW as possible<br />
          3. Config is saved and applied to all modules
        </p>
      </div>

      {/* Key Position Calibration Section */}
      <div style={sectionStyle}>
        <h4 style={sectionTitleStyle}>Key Position Calibration</h4>

        <div style={{ marginBottom: '12px' }}>
          <label style={labelStyle}>Target Module</label>
          <select
            value={selectedModule}
            onChange={(e) => setSelectedModule(parseInt(e.target.value))}
            disabled={!isConnected || loading}
            style={{ ...inputStyle, width: '100%' }}
          >
            {modules.length === 0 ? (
              <option value={0}>No modules found</option>
            ) : (
              modules.map(m => (
                <option key={m.id} value={m.id}>
                  Module {m.id} {m.online ? '(online)' : '(offline)'} {m.calibrated ? '' : '(needs calibration)'}
                </option>
              ))
            )}
          </select>
        </div>

        <button
          onClick={handleStartKeyCal}
          disabled={!isConnected || loading || modules.length === 0}
          style={{ ...buttonStyle, backgroundColor: '#fd7e14' }}
        >
          Start Key Calibration
        </button>

        <p style={{ fontSize: '11px', color: '#666', marginTop: '8px' }}>
          1. Release all keys (measuring idle position)<br />
          2. Press all keys fully<br />
          3. Release all keys (calculating range)
        </p>
      </div>

      {/* Module List */}
      <div style={sectionStyle}>
        <h4 style={sectionTitleStyle}>
          Registered Modules
          <button
            onClick={fetchModules}
            disabled={!isConnected}
            style={{ ...buttonStyle, padding: '2px 8px', fontSize: '10px', marginLeft: '8px' }}
          >
            Refresh
          </button>
        </h4>

        {modules.length === 0 ? (
          <p style={{ fontSize: '12px', color: '#666' }}>No modules registered</p>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {modules.map(m => (
              <div
                key={m.id}
                style={{
                  padding: '6px 10px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  backgroundColor: m.online ? '#d4edda' : '#f8d7da',
                  border: `1px solid ${m.online ? '#c3e6cb' : '#f5c6cb'}`,
                }}
              >
                <strong>#{m.id}</strong>
                <span style={{ marginLeft: '4px', color: m.online ? '#155724' : '#721c24' }}>
                  {m.online ? 'online' : 'offline'}
                </span>
                {!m.calibrated && (
                  <span style={{ marginLeft: '4px', color: '#856404' }}>!</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Styles
const sectionStyle: React.CSSProperties = {
  padding: '12px',
  backgroundColor: '#fff',
  border: '1px solid #dee2e6',
  borderRadius: '6px',
};

const sectionTitleStyle: React.CSSProperties = {
  margin: '0 0 12px 0',
  fontSize: '13px',
  fontWeight: 600,
  color: '#495057',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '11px',
  fontWeight: 500,
  color: '#666',
  marginBottom: '4px',
};

const inputStyle: React.CSSProperties = {
  padding: '6px 8px',
  fontSize: '12px',
  border: '1px solid #ced4da',
  borderRadius: '4px',
  width: '80px',
};

const inputGroupStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
};

const unitStyle: React.CSSProperties = {
  fontSize: '11px',
  color: '#666',
};

const buttonStyle: React.CSSProperties = {
  padding: '6px 12px',
  fontSize: '12px',
  fontWeight: 500,
  color: '#fff',
  backgroundColor: '#007bff',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
};

const warningStyle: React.CSSProperties = {
  padding: '10px',
  backgroundColor: '#fff3cd',
  border: '1px solid #ffeeba',
  borderRadius: '4px',
  color: '#856404',
  fontSize: '12px',
};
